import { Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

const featuredArticles = articles.filter((a) => a.featured);
const trendingArticles = articles.filter((a) => a.trending);

const faqData = [
  {
    question: '潘金莲是真实存在的历史人物吗？',
    answer: '历史上确实存在一位潘氏女性，她是北宋清河县武植（即"武大郎"的原型）的妻子。但与文学作品中截然不同，真实的潘氏是一位贤良淑德的大家闺秀，与武植夫妻恩爱、家庭美满。文学作品中的潘金莲形象是经过数百年民间传说和文学加工后的虚构人物。',
  },
  {
    question: '《水浒传》和《金瓶梅》中的潘金莲有什么不同？',
    answer: '《水浒传》中的潘金莲是一个功能性反派角色，存在主要为推动武松故事线，形象相对扁平；而《金瓶梅》中的潘金莲是全书核心人物，兰陵笑笑生赋予了她惊人的性格深度和复杂性，使她成为了中国文学史上最为立体的人物形象之一。',
  },
  {
    question: '为什么潘金莲会被污名化数百年？',
    answer: '潘金莲的污名化源于多重因素：一桩因同窗黄堂求资助不得而恶意散布的谣言；民间说书人的添油加醋；《水浒传》《金瓶梅》的文学加持；以及封建礼教对"不守妇道"女性的系统性污名化。直到1992年武氏后人立碑正名，历史真相才得以昭雪。',
  },
  {
    question: '如何看待当代为潘金莲"翻案"的思潮？',
    answer: '翻案不等于洗白——理解不等于认同，解释不等于辩护。当代翻案思潮的价值在于将潘金莲放回具体的历史语境中理解她的行为根源，揭示封建礼教对女性的系统性压迫。我们可以理解潘金莲行为背后的社会根源，同时仍然谴责她所犯下的罪行。',
  },
  {
    question: '潘金莲研究有什么当代意义？',
    answer: '潘金莲研究的意义超越了文学范畴。她作为一个文化符号，折射出中国传统文化中的性别观念、道德评判体系和社会权力结构。通过重新审视潘金莲，我们能够反思传统文化中的性别偏见，推动性别平等意识的提升，以及对历史叙事的批判性思考。',
  },
];

export default function Home() {
  const homepageSchema = {
    '@context': 'https://culturepan.github.io',
    '@type': 'WebSite',
    name: '潘金莲文化研究中心',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: '潘金莲：历史真相与文学形象全解析。深度解析潘金莲的历史原型、文学演变、社会背景与文化争议。',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${typeof window !== 'undefined' ? window.location.origin : ''}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://culturepan.github.io',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <main className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Ancient%20Chinese%20classical%20literature%20manuscripts%20with%20ink%20brush%20and%20traditional%20scrolls%20on%20aged%20wooden%20desk%2C%20warm%20golden%20sunlight%20streaming%20through%20paper%20window%2C%20scholarly%20Chinese%20study%20room%2C%20elegant%20minimal%20aesthetic%2C%20soft%20shadows%2C%20historical%20atmosphere%2C%20no%20text&width=1600&height=800&seq=hero-bg-home&orientation=landscape"
            alt="古典文学研究"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto text-center px-4 md:px-6">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white mb-6 leading-tight">
              潘金莲：历史真相与文学形象全解析
            </h1>
            <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto mb-8 leading-relaxed">
              中国古典文学中最复杂的女性形象之一。深度解析潘金莲的历史原型、文学演变、社会背景与文化争议，还原一个被误读千年的女性真实面貌。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/historical-prototype"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all duration-200 whitespace-nowrap text-sm"
              >
                开始探索
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-white/15 hover:bg-white/25 text-white font-medium rounded-lg backdrop-blur-sm transition-all duration-200 whitespace-nowrap text-sm"
              >
                了解我们
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                研究分类
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto">
                从多个维度深入探索潘金莲的完整面貌
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl p-6 hover:border-primary-200 transition-all duration-300 flex flex-col items-center text-center"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors duration-300">
                    <i className={`${cat.icon} text-2xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold font-heading text-foreground-950 mb-2">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {cat.description}
                  </p>
                  <span className="mt-4 text-sm font-medium text-primary-600 flex items-center gap-1 group-hover:gap-2 transition-all duration-200 whitespace-nowrap">
                    浏览文章
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                  精选文章
                </h2>
                <p className="text-foreground-600 text-sm">
                  编辑推荐的深度研究文章
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.slice(0, 6).map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </div>
        </section>

        {/* Trending Articles */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                  热门文章
                </h2>
                <p className="text-foreground-600 text-sm">
                  读者最关注的议题与讨论
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trendingArticles.slice(0, 6).map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-14 md:py-20 bg-accent-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-bold text-background-50 font-heading mb-2">120+</div>
                <div className="text-sm text-background-50/80">学术论文</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-background-50 font-heading mb-2">38</div>
                <div className="text-sm text-background-50/80">合作机构</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-background-50 font-heading mb-2">15</div>
                <div className="text-sm text-background-50/80">届学术会议</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-background-50 font-heading mb-2">50万+</div>
                <div className="text-sm text-background-50/80">读者群体</div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-600">
                关于潘金莲，这些可能是你最想了解的问题
              </p>
            </div>
            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <details key={index} className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-background-100 transition-colors list-none">
                    <span className="text-base font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-foreground-500 group-open:rotate-180 transition-transform duration-200">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20traditional%20Chinese%20library%20with%20wooden%20bookshelves%20filled%20with%20ancient%20books%2C%20warm%20reading%20lamp%20glow%2C%20scholarly%20atmosphere%2C%20quiet%20study%20space%2C%20soft%20amber%20lighting%2C%20academic%20setting&width=1600&height=600&seq=cta-bg-home&orientation=landscape"
            alt="学术研究"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/50"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center px-4 md:px-6">
            <h2 className="text-2xl md:text-4xl font-bold font-heading text-white mb-4">
              加入我们的学术探索之旅
            </h2>
            <p className="text-white/80 mb-8 leading-relaxed">
              关注潘金莲文化研究中心，获取最新的研究成果、学术动态与深度分析文章，与我们一起还原历史真相，探讨文化深意。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/literary-image"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all duration-200 whitespace-nowrap text-sm"
              >
                浏览文章
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-white/15 hover:bg-white/25 text-white font-medium rounded-lg backdrop-blur-sm transition-all duration-200 whitespace-nowrap text-sm"
              >
                联系我们
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}